import { useEffect, useState, type ReactNode } from 'react';
import { FoxyChat } from '@/components/foxy/FoxyChat';
import { FoxyDigest, type DigestData } from '@/components/foxy/FoxyDigest';
import { useAuth } from '@/hooks/use-auth';

export function FoxyPage(): ReactNode {
  const { user, accessToken } = useAuth();
  const tenantId = user?.tenantId ?? '';
  const [digest, setDigest] = useState<DigestData | null>(null);
  const [digestLoading, setDigestLoading] = useState(true);

  // Load digest data on mount (desktop sidebar)
  useEffect(() => {
    if (!tenantId || !accessToken) return;

    let cancelled = false;

    async function loadDigest(): Promise<void> {
      try {
        const res = await fetch('/api/foxy/quality?days=1', {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        const json = await res.json() as {
          success: boolean;
          data?: {
            total: number;
            thumbsUp: number;
            thumbsDown: number;
            escalated: number;
            autoAnswerRate: number;
          };
        };
        if (!cancelled && json.success && json.data) {
          setDigest({
            orders: { count: 0, revenue: 0 },
            newCustomers: 0,
            topProduct: null,
            bookings: { count: 0, upcoming: 0 },
            messages: {
              received: json.data.total,
              autoAnswered: json.data.total - json.data.escalated,
            },
          });
        }
      } catch {
        // Digest is optional — fail silently
      } finally {
        if (!cancelled) setDigestLoading(false);
      }
    }

    loadDigest();
    return () => { cancelled = true; };
  }, [tenantId, accessToken]);

  return (
    <div
      className="h-[calc(100dvh-3.5rem)]"
      dir="rtl"
      style={{ background: 'hsl(var(--background))' }}
    >
      {/* Page title for screen readers */}
      <h1 className="sr-only">פוקסי — העוזר שלך</h1>

      {/* Desktop: two-column layout | Mobile: full-screen chat */}
      <div className="flex h-full">
        {/* Chat area — always visible */}
        <div className="flex-1 md:flex-[0.6] h-full min-w-0">
          <FoxyChat tenantId={tenantId} />
        </div>

        {/* Digest sidebar — desktop only */}
        <div
          className="hidden md:flex md:flex-[0.4] flex-col border-s overflow-y-auto"
          style={{ borderColor: 'hsl(var(--border))' }}
        >
          {/* Sidebar header accent strip */}
          <div
            className="px-5 py-4 border-b shrink-0"
            style={{
              background: 'var(--brand-gradient-soft)',
              borderColor: 'hsl(var(--border))',
            }}
          >
            <p
              className="text-xs font-semibold uppercase tracking-widest"
              style={{ color: 'var(--brand-primary)' }}
            >
              סיכום יומי
            </p>
          </div>
          <div className="flex-1 p-4 overflow-y-auto">
            <FoxyDigest digest={digest} loading={digestLoading} />
          </div>
        </div>
      </div>
    </div>
  );
}
